'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy } from 'lucide-react';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';

const DETAIL_HOVER_CARD_Z_INDEX = 220;

type HoverCardPosition = 'auto' | 'top' | 'bottom';

type DetailHoverCardProps = React.HTMLAttributes<HTMLElement> & {
  as?: React.ElementType;
  /** Bold heading rendered at the top of the hover card. */
  title: string;
  /** Detail lines rendered under the title. Empty strings render as spacers;
   *  null/undefined entries are skipped. */
  lines?: Array<string | null | undefined>;
  /** Theme flag — this component lives outside ThemeContext consumers. */
  isDark?: boolean;
  hoverCardClassName?: string;
  hoverCardPosition?: HoverCardPosition;
  disabled?: boolean;
};

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  try {
    (ref as React.MutableRefObject<T | null>).current = value;
  } catch {
    // Ignore non-writable refs.
  }
}

const DetailHoverCard = React.forwardRef<HTMLElement, DetailHoverCardProps>(function DetailHoverCard(
  {
    as: Component = 'div',
    title,
    lines = [],
    isDark = false,
    hoverCardClassName = '',
    hoverCardPosition = 'auto',
    disabled = false,
    children,
    onMouseEnter,
    onMouseLeave,
    onFocusCapture,
    onBlurCapture,
    ...rest
  },
  forwardedRef,
) {
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const hoverCardRef = React.useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [resolvedPosition, setResolvedPosition] = React.useState<'top' | 'bottom'>(
    hoverCardPosition === 'top' ? 'top' : 'bottom'
  );
  const [hoverCardStyle, setHoverCardStyle] = React.useState<React.CSSProperties>({ top: 8, left: 8 });
  const [copied, setCopied] = React.useState(false);
  const hideTimeoutRef = React.useRef<number | null>(null);

  const tooltipLines = React.useMemo(
    () => lines.filter((line): line is string => typeof line === 'string'),
    [lines],
  );

  const copyText = React.useMemo(
    () => [title, '', ...tooltipLines].join('\n').trimEnd(),
    [title, tooltipLines],
  );

  const setCombinedRef = React.useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
    assignRef(forwardedRef, node);
  }, [forwardedRef]);

  const hideHoverCard = React.useCallback(() => {
    setIsVisible(false);
    setCopied(false);
  }, []);

  const cancelPendingHide = React.useCallback(() => {
    if (hideTimeoutRef.current != null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Delayed hide lets the pointer cross the small gap between the trigger and
  // the portal-mounted card so the copy button inside remains clickable.
  const scheduleHideHoverCard = React.useCallback(() => {
    cancelPendingHide();
    hideTimeoutRef.current = window.setTimeout(hideHoverCard, 250);
  }, [cancelPendingHide, hideHoverCard]);

  React.useEffect(() => () => cancelPendingHide(), [cancelPendingHide]);

  const handleCopyDetails = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (insecure context or denied permission).
    }
  }, [copyText]);

  const updateHoverCardPosition = React.useCallback(() => {
    if (!triggerRef.current || !hoverCardRef.current || typeof window === 'undefined') {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const cardRect = hoverCardRef.current.getBoundingClientRect();
    const gutter = 8;
    const spaceAbove = triggerRect.top - gutter;
    const spaceBelow = uiViewportHeight() - triggerRect.bottom - gutter;

    let nextPosition: 'top' | 'bottom' = hoverCardPosition === 'top' ? 'top' : 'bottom';

    if (hoverCardPosition === 'auto') {
      nextPosition = spaceBelow >= cardRect.height || spaceBelow >= spaceAbove ? 'bottom' : 'top';
    } else if (hoverCardPosition === 'top' && spaceAbove < cardRect.height && spaceBelow > spaceAbove) {
      nextPosition = 'bottom';
    } else if (hoverCardPosition === 'bottom' && spaceBelow < cardRect.height && spaceAbove > spaceBelow) {
      nextPosition = 'top';
    }

    const idealTop = nextPosition === 'top'
      ? triggerRect.top - cardRect.height - gutter
      : triggerRect.bottom + gutter;
    const maxTop = Math.max(gutter, uiViewportHeight() - cardRect.height - gutter);
    const clampedTop = Math.min(Math.max(gutter, idealTop), maxTop);
    const maxLeft = Math.max(gutter, uiViewportWidth() - cardRect.width - gutter);
    const clampedLeft = Math.min(Math.max(gutter, triggerRect.left), maxLeft);

    setResolvedPosition(nextPosition);
    setHoverCardStyle({
      top: clampedTop,
      left: clampedLeft,
      zIndex: DETAIL_HOVER_CARD_Z_INDEX,
    });
  }, [hoverCardPosition]);

  React.useEffect(() => {
    if (!isVisible || disabled) {
      return;
    }

    const frame = window.requestAnimationFrame(updateHoverCardPosition);
    const handleViewportChange = () => updateHoverCardPosition();
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;

      if (target && (triggerRef.current?.contains(target) || hoverCardRef.current?.contains(target))) {
        return;
      }

      hideHoverCard();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideHoverCard();
      }
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [disabled, hideHoverCard, isVisible, updateHoverCardPosition]);

  const handleMouseEnterInternal: React.MouseEventHandler<HTMLElement> = (event) => {
    onMouseEnter?.(event);
    cancelPendingHide();
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleMouseLeaveInternal: React.MouseEventHandler<HTMLElement> = (event) => {
    onMouseLeave?.(event);
    scheduleHideHoverCard();
  };

  const handleFocusCaptureInternal: React.FocusEventHandler<HTMLElement> = (event) => {
    onFocusCapture?.(event);
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleBlurCaptureInternal: React.FocusEventHandler<HTMLElement> = (event) => {
    onBlurCapture?.(event);
    const nextFocus = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextFocus) && !(nextFocus && hoverCardRef.current?.contains(nextFocus))) {
      scheduleHideHoverCard();
    }
  };

  const hoverCard = !disabled && isVisible ? (
    <div
      ref={hoverCardRef}
      style={hoverCardStyle}
      className={`fixed w-80 max-w-[min(26rem,calc(100vw-2rem))] rounded-xl border bg-white p-3 text-left shadow-2xl ring-1 ring-black/10 dark:border-gray-700 dark:bg-gray-900 dark:ring-white/10 ${resolvedPosition === 'top' ? 'origin-bottom' : 'origin-top'} ${hoverCardClassName}`}
      data-detail-hover-card="true"
      onMouseEnter={cancelPendingHide}
      onMouseLeave={scheduleHideHoverCard}
      onBlur={(event) => {
        const nextFocus = event.relatedTarget as Node | null;
        if (!event.currentTarget.contains(nextFocus) && !(nextFocus && triggerRef.current?.contains(nextFocus))) {
          scheduleHideHoverCard();
        }
      }}
    >
      <div className="space-y-1">
        <div className={`flex items-start justify-between gap-2 break-words text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <span className="min-w-0">{title}</span>
          <button
            type="button"
            onClick={handleCopyDetails}
            title={copied ? 'Copied' : 'Copy details'}
            aria-label={copied ? 'Copied' : 'Copy details to clipboard'}
            className={`shrink-0 rounded p-1 transition-colors ${
              copied
                ? isDark ? 'text-green-400' : 'text-green-600'
                : isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        {tooltipLines.map((line, index) => {
          if (line.trim().length === 0) {
            return <div key={`spacer-${index}`} className="h-1.5" />;
          }

          return (
            <div
              key={`line-${index}`}
              className={`break-words text-xs leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
            >
              {line}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <Component
        ref={setCombinedRef}
        onMouseEnter={handleMouseEnterInternal}
        onMouseLeave={handleMouseLeaveInternal}
        onFocusCapture={handleFocusCaptureInternal}
        onBlurCapture={handleBlurCaptureInternal}
        {...rest}
      >
        {children}
      </Component>

      {hoverCard && typeof document !== 'undefined'
        ? createPortal(hoverCard, document.body)
        : hoverCard}
    </>
  );
});

export default DetailHoverCard;
